import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useDeviceType } from '../../hooks/useDeviceType.ts';
import { useShortKeysNavigation } from '../../hooks/useShortKeysNavigation';

import ModalDiagramNotFound from '../../components/Modals/ModalDiagramNotFound/ModalDiagramNotFound';

import {
    selectIsBtnLeftPanelEnabled,
    selectIsBtnMinimapEnabled,

    selectIsBtnExportImageEnabled,
} from '../../redux/diagram/diagram.selectors';

import { selectIsDiagramCanvasDeleted, selectIsDiagramCanvasNotFound, selectDiagramTitle } from '../../redux/main/main.selectors';

import {
    mainCanvasStarted,
    clearDiagramErrors,
    toggleDiagramCreateNew,
    // loadNewProject,
    // loadSampleProjectDetails,
} from '../../redux/main/main.actions';
import {
    canvasStarted,
    fetchDiagram,
    toggleBtnModalShortcuts,
    // loadSampleProjectData,
    clearDiagramDetails,
} from '../../redux/diagram/diagram.actions';
import { joinRoom, leaveRoom } from '../../redux/collab/collab.actions';
import { getOrCreateDiagramDoc, destroyDiagramDoc, getDiagramAwareness } from '../../lib/yjs';

import LoaderBar from '../../components/Loaders/LoaderBar/LoaderBar';

import DraggableTable from '../../components/DraggableElements/DraggableTable/DraggableTable';
import DraggableNote from '../../components/DraggableElements/DraggableNote/DraggableNote';

import ControlsLeft from '../../components/Controls/ControlsLeft/ControlsLeft';
import ControlsBottom from '../../components/Controls/ControlsBottom/ControlsBottom';
import ControlsTopRight from '../../components/Controls/ControlsTopRight/ControlsTopRight';
import ControlsTopLeft from '../../components/Controls/ControlsTopLeft/ControlsTopLeft';
import ControlsFocusArrows from '../../components/Controls/ControlsFocusArrows/ControlsFocusArrows';
import DiagramSearch from '../../components/DiagramSearch/DiagramSearch';
import NoteLinkPreview from '../../components/NoteLinkPreview/NoteLinkPreview';
import CanvasScrollbars from '../../components/CanvasScrollbars/CanvasScrollbars';

import PanelLeft from '../../components/Panels/PanelLeft/PanelLeft';

import ModalFeedback from '../../components/Modals/ModalFeedback/ModalFeedback';
import ModalFeedbackThanks from '../../components/Modals/ModalFeedbackThanks/ModalFeedbackThanks.jsx';

import ModalKeyboardShortcuts from '../../components/Modals/ModalKeyboardShortcuts/ModalKeyboardShortcuts';

import ModalPricingPlanLimit from '../../components/Modals/ModalPricingPlanLimit/ModalPricingPlanLimit.jsx';
import ModalPricingPlanLimitCanvas from '../../components/Modals/ModalPricingPlanLimitCanvas/ModalPricingPlanLimitCanvas.jsx';
import ModalPricingPlanLimitMaxCanvas from '../../components/Modals/ModalPricingPlanLimitMaxCanvas/ModalPricingPlanLimitMaxCanvas.jsx';
import ModalPricingPlanUpgrade from '../../components/Modals/ModalPricingPlanUpgrade/ModalPricingPlanUpgrade';


import Minimap from '../../components/Minimap/Minimap';
import Canvas from '../../components/Canvas/Canvas';

import './Diagram.scss';
import NotifyMessage from '../../components/NotifyMessage/NotifyMessage';
import Notify from '../../components/Notify/Notify';
import NotifyButtonAI from '../../components/NotifyButtonAI/NotifyButtonAI';


import ModalDiagramDetails from '../../components/Modals/ModalDiagramDetails/ModalDiagramDetails';
import ModalDiagramShare from '../../components/Modals/ModalDiagramShare/ModalDiagramShare';
import ModalDiagramClone from '../../components/Modals/ModalDiagramClone/ModalDiagramClone';
import ModalDiagramDelete from '../../components/Modals/ModalDiagramDelete/ModalDiagramDelete';
import ModalDiagramDeleteSoft from '../../components/Modals/ModalDiagramDeleteSoft/ModalDiagramDeleteSoft';
import ModalDiagramCreate from '../../components/Modals/ModalDiagramCreate/ModalDiagramCreate';
import ModalDiagramCreateNew from '../../components/Modals/ModalDiagramCreateNew/ModalDiagramCreateNew';

import ModalDiagramExportSQL from '../../components/Modals/ModalDiagramExportSQL/ModalDiagramExportSQL.jsx';
import ModalLanguages from '../../components/Modals/ModalLanguages/ModalLanguages';
import ModalWorkspaceCreateNew from '../../components/Modals/ModalWorkspaceCreateNew/ModalWorkspaceCreateNew.jsx';


const Diagram = ({ isNew = false }) => {
    const { t, i18n } = useTranslation();
    const { diagramId } = useParams();
    const history = useHistory();

    const [diagramAwareness, setDiagramAwareness] = useState(null);

    const { isDesktop } = useDeviceType(); // mobile/tablet => false
    const { isMac } = useShortKeysNavigation();

    const isBtnLeftPanelEnabled = useSelector(selectIsBtnLeftPanelEnabled);
    const isBtnMinimapEnabled = useSelector(selectIsBtnMinimapEnabled);

    const isBtnExportImageEnabled = useSelector(selectIsBtnExportImageEnabled);

    const isDiagramCanvasDeleted = useSelector(selectIsDiagramCanvasDeleted);
    const isDiagramCanvasNotFound = useSelector(selectIsDiagramCanvasNotFound);

    const diagramTitle = useSelector(selectDiagramTitle);

    const loadingTimerRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);

    const dispatch = useDispatch();

    useEffect(() => {
        if (isNew) {
            // dispatch(loadNewProject());
            // } else if (isLoadSampleProject) {
            //     dispatch(loadSampleProjectDetails());
            //     dispatch(loadSampleProjectData());
        } else {
            dispatch(fetchDiagram(diagramId));
        }
        dispatch(canvasStarted());
        dispatch(mainCanvasStarted());

        return () => {
            dispatch(clearDiagramErrors());
            dispatch(clearDiagramDetails());
        };

    }, [diagramId, dispatch]);

    useEffect(() => {
        if (!diagramId) return undefined;
        getOrCreateDiagramDoc(diagramId);
        return () => {
            destroyDiagramDoc();
        };
    }, [diagramId]);

    useEffect(() => {
        // Block browser defaults for these combos (Ctrl/Cmd-only and Ctrl/Cmd+Shift).
        // Keystrokes here are claimed by the app — they should not trigger bookmarks,
        // view-source, downloads, DevTools, etc. while the diagram canvas is active.
        const ctrlStopCodes = new Set([
            'KeyQ', 'KeyY', 'KeyU', 'KeyI', 'KeyJ', 'Quote', 'KeyB',
            'KeyS', 'KeyP', 'KeyL', 'KeyG', 'KeyH',
            // Reserved for future app shortcuts:
            'KeyO', 'KeyM', 'Period', 'Comma', 'KeyF', 'KeyK',
        ]);
        const ctrlShiftStopCodes = new Set(['KeyI', 'KeyC']);

        const handleKeyDown = (e) => {
            const isCtrl = isMac ? e.metaKey : e.ctrlKey;

            if (isCtrl && e.shiftKey && (e.code === 'KeyO' || e.key.toLowerCase() === 'o')) {
                e.preventDefault();
                dispatch(toggleDiagramCreateNew('canvas'));
                return;
            }

            if (isCtrl && e.code === 'Slash') {
                e.preventDefault();
                dispatch(toggleBtnModalShortcuts());
                return;
            }

            if (isCtrl && e.shiftKey && ctrlShiftStopCodes.has(e.code)) {
                e.preventDefault();
                return;
            }

            if (isCtrl && !e.shiftKey && ctrlStopCodes.has(e.code)) {
                e.preventDefault();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [dispatch, isMac]);

    // Must be declared after the getOrCreateDiagramDoc effect so it runs after it
    // (same-component effects fire in declaration order).
    useEffect(() => {
        if (!diagramId) return;
        setDiagramAwareness(getDiagramAwareness());
    }, [diagramId]);

    useEffect(() => {
        if (!diagramId) return undefined;
        dispatch(joinRoom(diagramId));
        return () => {
            dispatch(leaveRoom());
        };
    }, [dispatch, diagramId]);

    useEffect(() => {
        if (!isDiagramCanvasDeleted) return undefined;
        const t = setTimeout(() => {
            history.push(`/diagrams`);
        }, 300);
        return () => clearTimeout(t);
    }, [isDiagramCanvasDeleted, history]);

    useEffect(() => {
        // set is page loading
        setIsLoading(true);

        // set timeout on page loading
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = setTimeout(() => {
            setIsLoading(false);
        }, 450);
        return () => { };
    }, []);

    useEffect(() => {
        if (isLoading) {
            document.title = t('n.default');
        } else {
            if (isDiagramCanvasNotFound) {
                document.title = t('m_diagram_not_found.header');
            } else {
                document.title = diagramTitle;
            }
        }
    }, [isLoading, diagramTitle, isDiagramCanvasNotFound, t, i18n.language]);

    useEffect(() => {
        if (isDiagramCanvasNotFound) {
            document.title = t('m_diagram_not_found.header');
        }
    }, [isDiagramCanvasNotFound, t, i18n.language]);

    const canvasRef = useRef(null);
    const minimapRef = useRef(null);

    // URL points at a diagram that can't be opened (not found / no access /
    // wrong URL). Render only the non-closable modal — skip the whole editor.
    if (isDiagramCanvasNotFound) return <ModalDiagramNotFound />;
    if (!isNew && isLoading) return null;

    return (
        <div className="Diagram">
            <div className="panel-panel">
                <div className="panel-board">
                    <DraggableTable />
                    <DraggableNote />
                    <ControlsTopLeft />
                    {isDesktop ? <ControlsTopRight awareness={diagramAwareness} /> : ''}
                    {true ? <ControlsLeft /> : ''}
                    <ControlsBottom canvasRef={canvasRef} minimapRef={minimapRef} />
                    <ControlsFocusArrows />
                    <DiagramSearch />
                    <NoteLinkPreview />
                    <CanvasScrollbars />

                    <PanelLeft isVisible={isBtnLeftPanelEnabled && !isBtnExportImageEnabled} />

                    <ModalKeyboardShortcuts />
                    <ModalLanguages />

                    <ModalFeedback />
                    <ModalFeedbackThanks />

                    <ModalDiagramDetails />
                    <ModalDiagramShare />
                    <ModalDiagramClone />
                    <ModalDiagramDeleteSoft />
                    <ModalDiagramDelete />

                <ModalPricingPlanLimit />
                    <ModalPricingPlanLimitCanvas />
                    <ModalPricingPlanLimitMaxCanvas />
                    <ModalPricingPlanUpgrade />

                    <ModalDiagramCreate />
                    <ModalDiagramCreateNew />
                    <ModalDiagramExportSQL />

                    <ModalWorkspaceCreateNew />

                    <Minimap ref={minimapRef} isOpen={isBtnMinimapEnabled && !isBtnExportImageEnabled} />

                    {/* <NotifyMessage /> */}
                    {/* <NotifyButtonAI /> */}
                    <Notify viewType='canvas' />

                    {Math.random() === 12 && <LoaderBar isLoading={isLoading} />}
                    <Canvas
                        key={diagramId}
                        isLoading={isLoading}
                        ref={canvasRef}
                        minimapRef={minimapRef}
                    />
                </div>
            </div>
        </div>
    );
};

export default Diagram;
